import { Button, Col, Form, Input, Modal, Row, Select } from 'antd';
import { bool, func, object } from 'prop-types';
import { useCallback, useRef, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import dynamic from 'next/dynamic';

import { bookAppointment } from 'actions/shops';
import FormInput from 'components/formInput';

import { InStoreSVG } from 'components/iconsSVG/inStore';
import { VirtualAppointmentSVG } from 'components/iconsSVG/virtualAppointment';
import { CloseModalSVG } from 'components/iconsSVG/close';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { NotifySVG } from 'components/iconsSVG/notify';
import { PrivacyPolicy } from 'components/privacyPolicy';

import { useI18n } from 'context/i18n';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { handleReCaptcha } from 'utils/recaptcha';
import { camelCaseToRegularString } from 'utils/StringUtils';

import { useValidate } from 'hooks';
import styles from './style.module.css';

const ThankYouMessage = dynamic(() => import('components/thankYouMessage').then((mod) => mod.ThankYouMessage));

const AppointmentModal = ({ store, visible = false, onClose = () => {}, storesByCountry = null }) => {
  const { t, locale } = useI18n();
  const validate = useValidate();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => handleReCaptcha(executeRecaptcha), [executeRecaptcha]);

  const [inStore, setInStore] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [filteredCity, setFilteredCity] = useState(null);
  const [filteredStore, setFilteredStore] = useState(null);
  const [filteredStoreId, setFilteredStoreId] = useState(null);
  const currentStore = filteredStore || store;
  const startedTyping = useRef(false);
  const formRef = useRef(null);

  const handleClose = () => {
    onClose();
    setSubmitted(false);
  };

  const handleSubmit = async (values) => {
    try {
      const token = await handleReCaptchaVerify();
      if (values?.privacy === true) {
        const result = await bookAppointment(locale, {
          name: values.name,
          email: values.email,
          telephone: values.phone,
          videocallAppointment: !inStore,
          storeCode: currentStore.id,
          informationDate: values.timeComment,
          captchaToken: token,
        });

        if (result === true) {
          dataLayerHandleEvent({
            event: 'personal_shopper',
            event_category: 'personal shopper',
            event_detail_1: store?.id || '',
            event_detail_2: inStore ? 'in-store' : 'virtual',
          });
          setSubmitted(true);
        }
      }
    } catch (e) {
      console.error(e);
      Modal.error({
        title: t('generico', 'error.api.modal.titulo', 'Se ha producido un error'),
        content: e,
      });
    }
  };

  const ModalTitle = () => (
    <>
      <NotifySVG />
      <p className={styles.memberText}>{t('ficha.producto', 'modal.members.only.title', 'Members Only')}</p>
      <p className={styles.modalTitle} dangerouslySetInnerHTML={{ __html: t('tiendas.locator', 'banner.title.appointment', 'Book your appointment') }} />
      <p className={styles.storeName}>{currentStore?.name || ''}</p>
      <p
        className={styles.modalSubtitle}
        dangerouslySetInnerHTML={{
          __html: t('tiendas.locator', 'book.subtitle.appointment', '***Make an appointment with a member of our team for a personalized shopping experience.'),
        }}
      />
    </>
  );

  const handleSelectCity = (filterCity) => {
    setFilteredCity(filterCity);
    setFilteredStoreId(null);
    setFilteredStore(null);
    formRef.current.resetFields([['store']]);
  };
  const handleSelectStore = (filterStore) => {
    const selectedStore = storesByCountry.stores.filter((storeItem) => storeItem.id === filterStore)[0];
    setFilteredStoreId(filterStore);
    setFilteredStore(selectedStore);
  };

  const SelectComponent =
    storesByCountry && storesByCountry.cities ?
      <Select
        value={filteredCity}
        showSearch
        className={styles.citySelector}
        // placeholder={t('tiendas.locator', 'select.city', 'Select a city')}
        onChange={handleSelectCity}
        allowClear={filteredCity !== null}
        suffixIcon={<ChevronSVG />}
        onClear={() => {
          // setCurrentPlace(currentCountry);
        }}
      >
        <Select.Option value={false}>-- {t('generico', 'all', 'All')} --</Select.Option>
        {storesByCountry.cities
          .filter((cityItem) => {
            const availableStoresInCity = storesByCountry?.stores?.filter(
              (storeItem) =>
                storeItem.cityName === cityItem && storeItem.bookingAvailability === true && (storeItem.inStoreAppointment || storeItem.videoCallAppointment),
            );
            return availableStoresInCity.length > 0;
          })
          .map((item, index) => (
            <Select.Option key={`city-option-${index}`} value={item}>
              {item}
            </Select.Option>
          ))}
      </Select>
    : null;

  const storesData =
    filteredCity ?
      storesByCountry?.stores?.filter(
        (storeItem) =>
          storeItem.cityName === filteredCity && storeItem.bookingAvailability === true && (storeItem.inStoreAppointment || storeItem.videoCallAppointment),
      )
    : storesByCountry?.stores || [];

  const SelectStoreComponent =
    storesByCountry && storesByCountry.stores ?
      <Select
        value={filteredStoreId}
        showSearch={false}
        className={styles.citySelector}
        // placeholder={t('tiendas.locator', 'select.store', 'Select a store')}
        onChange={handleSelectStore}
        allowClear={filteredStore !== null}
        suffixIcon={<ChevronSVG />}
        onClear={() => {
          // setCurrentPlace(currentCountry);
        }}
      >
        {storesData.map((item) => (
          <Select.Option key={`store-option-${item.id}`} value={item.id}>
            {item.name}
          </Select.Option>
        ))}
      </Select>
    : null;
  const handleOnFieldsChange = () => {
    if (!startedTyping.current && store?.id) {
      dataLayerHandleEvent({
        event: 'start_personal_shopper',
        event_category: 'personal shopper',
        event_detail_1: store?.id || '',
        event_detail_2: store?.name?.toLowerCase() || '',
      });
      startedTyping.current = true;
    }
  };
  const onFinishFailed = ({ errorFields }) => {
    errorFields.map((errorField) => {
      const errorFieldName = errorField?.name && errorField?.name?.length ? errorField?.name[0] : '';
      dataLayerHandleEvent({
        event: 'error_personal_shopper',
        event_category: 'personal shopper',
        event_detail_1: store?.id || '',
        event_detail_2: camelCaseToRegularString(errorFieldName),
      });
      return true;
    });
  };
  const handleOnClickSend = () => {
    dataLayerHandleEvent({
      event: 'try_personal_shopper',
      event_category: 'personal shopper',
      event_detail_1: store?.id || '',
      event_detail_2: store?.name?.toLowerCase() || '',
    });
  };
  return (
    <Modal
      className={`${styles.appointmentModal} ${submitted ? styles.greenModal : ''}`}
      width={'640px'}
      title={submitted ? <ThankYouMessage type="appointment" /> : <ModalTitle />}
      visible={visible}
      centered={true}
      closable={true}
      destroyOnClose={true}
      onCancel={handleClose}
      footer={null}
      closeIcon={<CloseModalSVG />}
    >
      {!submitted && (
        <>
          <Form
            className={`${styles.modalContent} customForm`}
            onFinish={handleSubmit}
            onFinishFailed={onFinishFailed}
            onFieldsChange={handleOnFieldsChange}
            ref={formRef}
          >
            {storesByCountry && storesByCountry?.stores && storesByCountry?.cities && (
              <Row className={styles.cityStoreRow} gutter={[8, 0]}>
                <Col xs={12}>
                  <FormInput name="city" floatLabel={t('tiendas.locator', 'select.city', 'Select a city')}>
                    {SelectComponent}
                  </FormInput>
                </Col>
                <Col xs={12}>
                  <FormInput name="store" required rules={validate('requiredOption')} floatLabel={t('tiendas.locator', 'select.store', 'Select a store')}>
                    {SelectStoreComponent}
                  </FormInput>
                </Col>
              </Row>
            )}
            <p className={styles.modalTabs}>
              <Button
                disabled={!currentStore?.inStoreAppointment}
                className={`${styles.modalTab} ${inStore ? styles.active : ''}`}
                onClick={() => setInStore(true)}
                icon={<InStoreSVG />}
                type={inStore ? 'primary' : 'default'}
              >
                In-Store
              </Button>
              <Button
                disabled={!currentStore?.videoCallAppointment}
                className={`${styles.modalTab} ${!inStore ? styles.active : ''}`}
                onClick={() => setInStore(false)}
                icon={<VirtualAppointmentSVG />}
                type={inStore ? 'default' : 'primary'}
              >
                Virtual
              </Button>
            </p>
            <FormInput name="name" rules={validate('name')} required floatLabel={t('generico', 'input.name', 'Name')}>
              <Input placeholder="" className={styles.input} />
            </FormInput>
            <div className={styles.inputGroup}>
              <FormInput name="email" rules={validate('email')} required floatLabel={t('generico', 'input.email', 'Email')}>
                <Input className={styles.input} />
              </FormInput>
              <FormInput name="phone" rules={validate('noStrictTelephone')} required floatLabel={t('generico', 'input.phone', 'Phone')}>
                <Input className={styles.input} />
              </FormInput>
            </div>
            <div className={styles.dayTimeComment}>
              <FormInput
                name="timeComment"
                rules={validate('textArea')}
                required
                floatLabel={t('generico', 'preferred.day.time', 'Let us know your preferred day and time')}
              >
                <textarea />
              </FormInput>
            </div>
            <PrivacyPolicy origin="cita.previa" checkBoxClass={styles.appointmentCheckBox} wrapperClass={styles.agreement} hideConditions />
            <Button onClick={handleOnClickSend} type="primary" disabled={!currentStore} className={styles.submitButton} htmlType="submit">
              {t('generico', 'send', 'Send')}
            </Button>
          </Form>
        </>
      )}
    </Modal>
  );
};

AppointmentModal.propTypes = {
  store: object,
  visible: bool,
  onClose: func,
  storesByCountry: object,
};

const WrapperAppointmentModal = ({ visible = false, ...props }) => {
  const { locale } = useI18n();

  return visible ?
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.RECAPTCHA_KEY}
        language={locale}
        useRecaptchaNet
        scriptProps={{
          async: false,
          defer: false,
          appendTo: 'body',
          nonce: undefined,
        }}
      >
        <AppointmentModal visible={visible} {...props} />
      </GoogleReCaptchaProvider>
    : <AppointmentModal visible={visible} {...props} />;
};

WrapperAppointmentModal.propTypes = {
  store: object,
  visible: bool,
  onClose: func,
  storesByCountry: object,
};

export default WrapperAppointmentModal;
